import { Alert } from "@mantine/core";
import type { MRT_ColumnDef, MRT_RowData } from "@ngt/mantine-react-table";
import type { ResponseStatus } from "@servicestack/client";
import {
    useCallback,
    useMemo,
    type ComponentProps,
    type ComponentType,
    type ReactNode,
} from "react";
import { Link as RouterLink } from "react-router-dom";
import type { Permission } from "src/api/Tmd.dtos";
import AppSettings from "src/AppSettings";
import DataGrid, {
    DefaultDataGridProps,
    mergeDataGridBodyRowProps,
    type DataGridBodyRowProps,
} from "src/components/common/dataGrid/DataGrid";
import { LayoutPermissionMode, type ILayoutItemContext } from "src/contexts/layouts/LayoutContext";
import Page from "src/components/layouts/pages/Page";
import PageTitle from "src/components/layouts/pages/PageTitle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { IDataGridFilter } from "../../common/dataGrid/DataGridFilter";
import { useDebouncedValue, useSessionStorage } from "@mantine/hooks";
import DataGridFilter from "../../common/dataGrid/DataGridFilter";
import dataGridConstants from "src/constants/dataGridConstants";

export { createInactiveDataGridBodyRowProps } from "src/components/common/dataGrid/DataGrid";

export type CollectionDataGridPageBeforeGridProps<TData extends object> = {
    data: TData[];
};

export type CollectionDataGridPageAfterGridProps<TData extends object> = {
    data: TData[];
};

export type CollectionDataGridPageGridProps<TData extends object> = {
    data: TData[];
    columns: MRT_ColumnDef<TData>[];
    getRowHref?: (row: TData) => string | undefined;
    mantineTableBodyRowProps?: DataGridBodyRowProps<TData>;
};

export interface ICollectionDataGridPageProps<
    TData extends object,
    TBeforeGridComponent extends ComponentType<CollectionDataGridPageBeforeGridProps<TData>> =
        ComponentType<CollectionDataGridPageBeforeGridProps<TData>>,
    TAfterGridComponent extends ComponentType<CollectionDataGridPageAfterGridProps<TData>> =
        ComponentType<CollectionDataGridPageAfterGridProps<TData>>,
> {
    gridKey: string;
    title: string;
    data: TData[];
    error?: ResponseStatus;
    loading: boolean;
    columns: MRT_ColumnDef<TData>[];
    viewPermission: Permission;
    filterFn?: (row: TData, filter: IDataGridFilter) => boolean;
    hideTextFilter?: boolean;
    hideInactiveFilter?: boolean;
    emptyMessage?: string;
    layoutContext?: ILayoutItemContext;
    layoutPermissionMode?: LayoutPermissionMode;
    actionButtons?: ReactNode;
    beforeGridComponent?: TBeforeGridComponent;
    beforeGridProps?: Omit<
        ComponentProps<TBeforeGridComponent>,
        keyof CollectionDataGridPageBeforeGridProps<TData>
    >;
    afterGridComponent?: TAfterGridComponent;
    afterGridProps?: Omit<
        ComponentProps<TAfterGridComponent>,
        keyof CollectionDataGridPageAfterGridProps<TData>
    >;
    gridComponent?: ComponentType<CollectionDataGridPageGridProps<TData>>;
    getRowHref?: (row: TData) => string | undefined;
    mantineTableBodyRowProps?: DataGridBodyRowProps<TData>;
}

interface ICollectionDataGridPageContentProps<
    TData extends object,
    TBeforeGridComponent extends ComponentType<CollectionDataGridPageBeforeGridProps<TData>> =
        ComponentType<CollectionDataGridPageBeforeGridProps<TData>>,
    TAfterGridComponent extends ComponentType<CollectionDataGridPageAfterGridProps<TData>> =
        ComponentType<CollectionDataGridPageAfterGridProps<TData>>,
> {
    title: string;
    useFilter: boolean;
    hideTextFilter: boolean;
    hideInactiveFilter: boolean;
    formFilter: IDataGridFilter;
    setFormFilter: (formFilter: IDataGridFilter) => void;
    data: TData[];
    error?: ResponseStatus;
    columns: MRT_ColumnDef<TData>[];
    emptyMessage?: string;
    actionButtons?: ReactNode;
    beforeGridComponent?: TBeforeGridComponent;
    beforeGridProps?: Omit<
        ComponentProps<TBeforeGridComponent>,
        keyof CollectionDataGridPageBeforeGridProps<TData>
    >;
    afterGridComponent?: TAfterGridComponent;
    afterGridProps?: Omit<
        ComponentProps<TAfterGridComponent>,
        keyof CollectionDataGridPageAfterGridProps<TData>
    >;
    gridComponent?: ComponentType<CollectionDataGridPageGridProps<TData>>;
    getRowHref?: (row: TData) => string | undefined;
    mantineTableBodyRowProps?: DataGridBodyRowProps<TData>;
}

const CollectionDataGridPageContent = <
    TData extends object,
    TBeforeGridComponent extends ComponentType<CollectionDataGridPageBeforeGridProps<TData>> =
        ComponentType<CollectionDataGridPageBeforeGridProps<TData>>,
    TAfterGridComponent extends ComponentType<CollectionDataGridPageAfterGridProps<TData>> =
        ComponentType<CollectionDataGridPageAfterGridProps<TData>>,
>({
    title,
    useFilter,
    hideInactiveFilter,
    hideTextFilter,
    formFilter,
    setFormFilter,
    data,
    error,
    columns,
    emptyMessage,
    actionButtons,
    beforeGridComponent,
    beforeGridProps,
    afterGridComponent,
    afterGridProps,
    gridComponent,
    getRowHref,
    mantineTableBodyRowProps: configuredBodyRowProps,
}: ICollectionDataGridPageContentProps<TData, TBeforeGridComponent, TAfterGridComponent>) => {
    const entityName = useMemo(() => title.toLowerCase(), [title]);
    const BeforeGridComponent = beforeGridComponent as any;
    const AfterGridComponent = afterGridComponent as any;
    const GridComponent = gridComponent;

    const showPagination = useMemo(
        () => data.length > dataGridConstants.defaults.pageSize,
        [data.length],
    );

    const mantineTableBodyRowProps = useCallback(
        (rowProps) => {
            const { row } = rowProps;
            const href = getRowHref?.(row.original);

            return mergeDataGridBodyRowProps(rowProps, configuredBodyRowProps, {
                ...(href ? { component: RouterLink, to: href } : {}),
                style: {
                    textDecoration: "none",
                    cursor: href ? "pointer" : undefined,
                },
            });
        },
        [configuredBodyRowProps, getRowHref],
    );

    const initialState = useMemo(
        () => ({
            ...DefaultDataGridProps.initialState,
            pagination: showPagination
                ? {
                      pageSize: dataGridConstants.defaults.pageSize,
                      pageIndex: 0,
                  }
                : undefined,
        }),
        [showPagination],
    );

    return (
        <>
            <PageTitle title={title}>
                {useFilter && (!hideInactiveFilter || !hideTextFilter) && (
                    <DataGridFilter
                        hideFilter={hideTextFilter}
                        hideActiveToggle={hideInactiveFilter}
                        filter={formFilter}
                        onFilter={setFormFilter}
                        debounceMs={AppSettings.dataGrid.filterDebounceMs}      
                    />
                )}
                {actionButtons}
            </PageTitle>
            {error && (
                <Alert
                    title="Error"
                    color="error"
                    icon={<FontAwesomeIcon icon={AppSettings.icons.error.medium} />}
                >
                    {error.message}
                </Alert>
            )}
            {BeforeGridComponent && <BeforeGridComponent {...beforeGridProps} data={data} />}
            {!error && data.length === 0 && (
                <Alert
                    color="information"
                    icon={<FontAwesomeIcon icon={AppSettings.icons.information} />}
                >
                    {emptyMessage ?? `There are currently no ${entityName.toLowerCase()} to view.`}
                </Alert>
            )}
            {!error &&
                data.length > 0 &&
                (GridComponent ? (
                    <GridComponent
                        data={data}
                        columns={columns}
                        getRowHref={getRowHref}
                        mantineTableBodyRowProps={configuredBodyRowProps}
                    />
                ) : (
                    <DataGrid
                        data={data}
                        columns={columns as unknown as MRT_ColumnDef<MRT_RowData, unknown>[]}
                        mantineTableBodyRowProps={mantineTableBodyRowProps}
                        initialState={initialState}
                        enablePagination={showPagination}
                        enableBottomToolbar={showPagination}
                    />
                ))}
            {AfterGridComponent && <AfterGridComponent {...afterGridProps} data={data} />}
        </>
    );
};

const CollectionDataGridPage = <
    TData extends object,
    TBeforeGridComponent extends ComponentType<CollectionDataGridPageBeforeGridProps<TData>> =
        ComponentType<CollectionDataGridPageBeforeGridProps<TData>>,
    TAfterGridComponent extends ComponentType<CollectionDataGridPageAfterGridProps<TData>> =
        ComponentType<CollectionDataGridPageAfterGridProps<TData>>,
>({
    gridKey,
    title,
    data,
    loading,
    error,
    columns: columnsOrFn,
    viewPermission,
    filterFn,
    hideInactiveFilter,
    hideTextFilter,
    emptyMessage,
    layoutContext,
    layoutPermissionMode,
    actionButtons,
    beforeGridComponent,
    beforeGridProps,
    afterGridComponent,
    afterGridProps,
    gridComponent,
    getRowHref,
    mantineTableBodyRowProps,
}: ICollectionDataGridPageProps<TData, TBeforeGridComponent, TAfterGridComponent>) => {
    const columns = useMemo(() => {
        if (columnsOrFn instanceof Function) {
            return columnsOrFn(data);
        }

        return columnsOrFn;
    }, [columnsOrFn, data]);

    const [formFilter, setFormFilter] = useSessionStorage<IDataGridFilter>({
        key: `${gridKey}-filter`,
        defaultValue: { search: "", showInactive: false },
    });

    const [debouncedFormFilter] = useDebouncedValue(
        formFilter,
        AppSettings.dataGrid.filterDebounceMs,
    );

    const filteredData = useMemo(() => {
        const rows = data ?? [];

        const filterToUse: IDataGridFilter = {
            search: debouncedFormFilter.search?.toLocaleLowerCase(),
            showInactive: debouncedFormFilter.showInactive,
        };

        return filterFn ? rows.filter((x) => filterFn(x, filterToUse)) : rows;
    }, [filterFn, data, debouncedFormFilter]);

    return (
        <Page
            loading={loading}
            permission={viewPermission}
            layoutPermissionMode={layoutPermissionMode ?? LayoutPermissionMode.Any}
            context={layoutContext ?? {}}
            component={
                CollectionDataGridPageContent<TData, TBeforeGridComponent, TAfterGridComponent>
            }
            componentProps={{
                title,
                useFilter: !!filterFn,
                hideInactiveFilter: !!hideInactiveFilter,
                hideTextFilter: !!hideTextFilter,
                formFilter,
                setFormFilter,
                data: filteredData,
                error: error,
                columns,
                emptyMessage,
                actionButtons,
                beforeGridComponent,
                beforeGridProps,
                afterGridComponent,
                afterGridProps,
                gridComponent,
                getRowHref,
                mantineTableBodyRowProps,
            }}
        />
    );
};

export default CollectionDataGridPage;
